import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCw, MessageCircle, User, Phone, Clock, Search,
  ArrowLeft, Send, Bot, UserCheck, AlertTriangle, X,
  Activity, Radio, Pause, Play, ChevronDown
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusConfig = {
  active: { label: "Active", color: "bg-emerald-500", textColor: "text-emerald-700", bgLight: "bg-emerald-50 border-emerald-200" },
  human_takeover: { label: "Human", color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-50 border-blue-200" },
  handoff: { label: "Handoff", color: "bg-amber-500", textColor: "text-amber-700", bgLight: "bg-amber-50 border-amber-200" },
  closed: { label: "Closed", color: "bg-slate-400", textColor: "text-slate-500", bgLight: "bg-slate-50 border-slate-200" },
};

const agentStyle = {
  tanya: { dot: "bg-violet-500", ring: "ring-violet-200" },
  jyoti: { dot: "bg-rose-500", ring: "ring-rose-200" },
};

function timeAgo(timestamp) {
  if (!timestamp) return "";
  const now = new Date();
  const t = new Date(timestamp);
  const diff = Math.floor((now - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ConversationCard({ conv, isSelected, onClick }) {
  const cfg = statusConfig[conv.status] || statusConfig.closed;
  const agent = agentStyle[conv.agent] || agentStyle.tanya;
  const lastRole = conv.last_message?.role;
  const lastContent = conv.last_message?.content || "No messages yet";
  const waitingForReply = lastRole === "assistant";

  return (
    <div
      data-testid={`monitor-card-${conv.lead_id}`}
      onClick={onClick}
      className={`cursor-pointer rounded-lg border p-3 transition-all hover:shadow-md ${
        isSelected ? "ring-2 ring-slate-900 shadow-lg" : "hover:border-slate-300"
      } ${cfg.bgLight}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.color}`} />
          <span className="text-sm font-semibold text-slate-900 truncate">{conv.name}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className={`w-1.5 h-1.5 rounded-full ${agent.dot}`} />
          <span className="text-[10px] text-slate-500 capitalize">{conv.agent}</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 mb-2">
        <Phone className="w-3 h-3 text-slate-400" />
        <span className="text-xs text-slate-500">{conv.phone}</span>
        {conv.city && (
          <>
            <span className="text-slate-300">|</span>
            <span className="text-xs text-slate-500">{conv.city}</span>
          </>
        )}
      </div>

      <div className="bg-white/60 rounded px-2 py-1.5 mb-2">
        <div className="flex items-center gap-1 mb-0.5">
          {lastRole === "user" ? (
            <User className="w-3 h-3 text-slate-500" />
          ) : (
            <Bot className="w-3 h-3 text-violet-500" />
          )}
          <span className="text-[10px] text-slate-400">{lastRole === "user" ? "Customer" : "AI Agent"}</span>
        </div>
        <p className="text-xs text-slate-700 line-clamp-2 leading-relaxed">{lastContent}</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${cfg.textColor}`}>{cfg.label}</Badge>
          <span className="text-[10px] text-slate-400">{conv.message_count} msgs</span>
        </div>
        <span className="text-[10px] text-slate-400">{timeAgo(conv.updated_at)}</span>
      </div>

      {waitingForReply && conv.status === "active" && (
        <div className="mt-1.5 flex items-center gap-1">
          <div className="w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-[10px] text-amber-600">Waiting for customer reply</span>
        </div>
      )}
    </div>
  );
}

function ChatPanel({ leadId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-agent/conversations/${leadId}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  // Auto-refresh every 8 seconds
  useEffect(() => {
    const interval = setInterval(fetchDetail, 8000);
    return () => clearInterval(interval);
  }, [fetchDetail]);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
    </div>
  );

  const conv = data?.conversation;
  const lead = data?.lead;
  const messages = conv?.messages || [];

  return (
    <div data-testid="chat-panel" className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="lg:hidden p-1 hover:bg-slate-100 rounded">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{lead?.name || "Unknown"}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-slate-500">{lead?.phone}</span>
              {lead?.city && <span className="text-xs text-slate-400">| {lead.city}</span>}
              <Badge variant="outline" className="text-[10px]">{conv?.agent_name}</Badge>
              <Badge variant="outline" className={`text-[10px] ${statusConfig[conv?.status]?.textColor || ""}`}>
                {statusConfig[conv?.status]?.label || conv?.status}
              </Badge>
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchDetail} data-testid="refresh-chat">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50/50">
        {messages.map((msg, i) => {
          const isUser = msg.role === "user";
          const imageUrl = msg.metadata?.image_url;
          const imageB64 = msg.metadata?.image_base64;

          return (
            <div key={i} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-2 ${
                isUser ? "bg-white border border-slate-200" : "bg-slate-800 text-white"
              }`}>
                <div className="flex items-center gap-1 mb-1">
                  {isUser ? <User className="w-3 h-3 text-slate-400" /> : <Bot className="w-3 h-3 text-violet-300" />}
                  <span className={`text-[10px] ${isUser ? "text-slate-400" : "text-slate-400"}`}>
                    {isUser ? "Customer" : msg.agent || "AI"}
                  </span>
                  <span className={`text-[10px] ml-auto ${isUser ? "text-slate-300" : "text-slate-500"}`}>
                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
                {(imageUrl || imageB64) && (
                  <img
                    src={imageB64 ? `data:image/jpeg;base64,${imageB64}` : imageUrl}
                    alt="Shared"
                    className="max-w-full rounded-lg mb-1 max-h-48 object-contain"
                  />
                )}
                <p className={`text-xs leading-relaxed whitespace-pre-wrap ${isUser ? "text-slate-700" : "text-slate-100"}`}>
                  {msg.content}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}

function SchedulerPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/eligible-leads/auto-schedule/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/eligible-leads/auto-schedule`, {
        batch_size: 10,
        pause_minutes: 10,
        template_id: "eligible_chat_temp",
      }, { withCredentials: true });
      await fetchStatus();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to start scheduler");
    } finally { setLoading(false); }
  };

  const handleStop = async () => {
    try {
      await axios.post(`${API}/eligible-leads/auto-schedule/stop`, {}, { withCredentials: true });
      await fetchStatus();
    } catch (e) { console.error(e); }
  };

  if (!status) return null;

  const progress = status.total_to_process > 0
    ? Math.round(((status.processed + status.failed) / status.total_to_process) * 100)
    : 0;

  return (
    <Card data-testid="scheduler-panel" className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-800">Campaign Scheduler</h3>
          </div>
          {status.running ? (
            <Button variant="destructive" size="sm" onClick={handleStop} data-testid="stop-scheduler-btn">
              <Pause className="w-3 h-3 mr-1" /> Stop
            </Button>
          ) : (
            <Button size="sm" onClick={handleStart} disabled={loading} data-testid="start-scheduler-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Play className="w-3 h-3 mr-1" /> {loading ? "Starting..." : "Start Campaign"}
            </Button>
          )}
        </div>

        {status.running && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {status.paused ? (
                <Badge className="bg-amber-100 text-amber-700 text-[10px]">Paused ({status.pause_minutes}m)</Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                  <Radio className="w-2.5 h-2.5 mr-1 animate-pulse" /> Processing
                </Badge>
              )}
              <span className="text-xs text-slate-500">
                Batch {status.current_batch}/{status.total_batches}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>{status.processed} sent | {status.failed} failed</span>
              <span>{progress}% complete</span>
            </div>
            {status.next_batch_at && status.paused && (
              <p className="text-[10px] text-amber-600">Next batch at: {new Date(status.next_batch_at).toLocaleTimeString("en-IN")}</p>
            )}
          </div>
        )}

        {status.completed && !status.running && (
          <div className="bg-emerald-50 rounded-lg p-2 text-xs text-emerald-700">
            Campaign completed. {status.processed} messages sent, {status.failed} failed.
          </div>
        )}

        {!status.running && !status.completed && status.processed === 0 && (
          <p className="text-xs text-slate-500">Not running. Click "Start Campaign" to begin batch processing eligible leads.</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function WhatsAppMonitorPage({ user, logout }) {
  const [conversations, setConversations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const fetchMonitor = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-agent/live-monitor?limit=300`, { withCredentials: true });
      setConversations(res.data.conversations || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonitor();
  }, [fetchMonitor]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchMonitor, 8000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchMonitor]);

  // Filter conversations
  const filtered = conversations.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q) ||
        (c.city || "").toLowerCase().includes(q) ||
        (c.lead_id || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <DashboardLayout user={user} logout={logout}>
      <div data-testid="whatsapp-monitor-page" className="h-[calc(100vh-64px)] flex flex-col">
        {/* Top Bar */}
        <div className="px-4 py-3 border-b bg-white flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                <Radio className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-900 tracking-tight">Live WhatsApp Monitor</h1>
                <p className="text-xs text-slate-500">
                  {summary ? `${summary.active} active | ${summary.human_takeover} human | ${summary.total} total` : "Loading..."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                data-testid="toggle-auto-refresh"
                className={autoRefresh ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
              >
                {autoRefresh ? <Radio className="w-3 h-3 mr-1 animate-pulse" /> : <Pause className="w-3 h-3 mr-1" />}
                {autoRefresh ? "Live" : "Paused"}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchMonitor} data-testid="refresh-monitor-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Scheduler Panel */}
          <SchedulerPanel />

          {/* Filters */}
          <div className="flex items-center gap-2 mt-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                data-testid="monitor-search"
                className="pl-8 h-8 text-xs"
                placeholder="Search name, phone, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {["all", "active", "human_takeover", "handoff", "closed"].map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? "default" : "outline"}
                size="sm"
                className={`text-xs h-7 px-2 ${statusFilter === s ? "bg-slate-800 text-white" : ""}`}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "All" : statusConfig[s]?.label || s}
                {s === "all" ? ` (${conversations.length})` : ` (${conversations.filter(c => c.status === s).length})`}
              </Button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Conversation Grid */}
          <div className={`${selectedId ? "hidden lg:block lg:w-[420px]" : "w-full"} overflow-y-auto p-3 border-r bg-slate-50/30`}>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-sm text-slate-400">No conversations found</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                {filtered.map((conv) => (
                  <ConversationCard
                    key={conv.lead_id}
                    conv={conv}
                    isSelected={selectedId === conv.lead_id}
                    onClick={() => setSelectedId(conv.lead_id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Chat Detail Panel */}
          {selectedId && (
            <div className="flex-1 bg-white">
              <ChatPanel leadId={selectedId} onClose={() => setSelectedId(null)} />
            </div>
          )}

          {!selectedId && (
            <div className="hidden lg:flex flex-1 items-center justify-center bg-white">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select a conversation to view the chat</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
